import { Company } from '@/api/model/company'
import { User } from '@/api/model/user'
import { BaseModel } from '@/api/base-model'
import { CredentialsType, EmmaDefaults, ReferralStatus } from '@/api/types'
import { CredentialsAPI } from '@/api/backend-client'
import { v4 as uuid } from 'uuid'
import { Branch } from '@/api/model/branch'
import { ProductCategory } from '@/api/model/product-category'

export class Credentials extends BaseModel<Credentials> {
    protected collectionName = CredentialsAPI.collectionName

    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public credentialsType: CredentialsType,
    ) { super(id) }

    static fromData(data: any): Credentials {
        if (data === null || typeof data === 'string' || data instanceof Credentials) {
            return data
        }

        switch (data.credentialsType) {
            case CredentialsType.ACCELERATE: {
                return AccelerateCredentials.fromData(data)
            }

            case CredentialsType.ACTIVE_CAMPAIGN: {
                return ActiveCampaignCredentials.fromData(data)
            }

            case CredentialsType.MAILCHIMP: {
                return MailchimpCredentials.fromData(data)
            }

            case CredentialsType.REALWORKS: {
                return RealworksCredentials.fromData(data)
            }

            case CredentialsType.TYPEFORM: {
                return TypeformCredentials.fromData(data)
            }

            default: {
                return null
            }
        }
    }
}

export class AccelerateCredentials extends Credentials {
    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public apiKey: string,
    ) { super(company, id, enabled, createdAt, createdBy, updatedAt, updatedBy, CredentialsType.ACCELERATE) }

    static fromData(data: any): AccelerateCredentials {
        return new AccelerateCredentials(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.enabled,
            data.createdAt,
            data.createdBy ? User.fromData(data.createdBy) : null,
            data.updatedAt,
            data.updatedBy ? User.fromData(data.updatedBy) : null,
            data.apiKey,
        )
    }

    // Convince Typescript that it is indeed an instance of the sub class
    clone(): AccelerateCredentials {
        return super.clone() as AccelerateCredentials
    }
}

export class ActiveCampaignCredentials extends Credentials {
    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public includeInternalContacts: boolean,
        public includeExternalContacts: boolean,
        public includeRealworksRelaties: boolean,
        public syncedRealworksKenmerken: string[],
        public excludedRealworksKenmerken: string[],
        public syncedRealworksRelatietypes: string[],
        public syncedRealworksStatus: boolean|null,
        public updatedFrom: Date,
        public updatedUntil: Date,
        public apiUrl: string|null,
        public apiKey: string|null,
        public eventTrackingAccountId: string|null,
        public eventTrackingKey: string|null,
        public defaultTeams: object,
        public defaultStatus: string,
        public syncedRealworksAgendatypes: string[],
        public syncedRealworksTaaktypes: string[],
        public listId: string|null,
        public agendaListId: string|null,
        public taakListId: string|null,
        public viewingListId: string|null,
        public emmaListId: string|null,
        public emmaDefaults: EmmaDefaults,
    ) { super(company, id, enabled, createdAt, createdBy, updatedAt, updatedBy, CredentialsType.ACTIVE_CAMPAIGN) }

    static fromData(data: any): ActiveCampaignCredentials {
        return new ActiveCampaignCredentials(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.enabled,
            data.createdAt,
            data.createdBy ? User.fromData(data.createdBy) : null,
            data.updatedAt,
            data.updatedBy ? User.fromData(data.updatedBy) : null,
            data.includeInternalContacts,
            data.includeExternalContacts,
            data.includeRealworksRelaties,
            data.syncedRealworksKenmerken,
            data.excludedRealworksKenmerken,
            data.syncedRealworksRelatietypes,
            data.syncedRealworksStatus,
            data.updatedFrom,
            data.updatedUntil,
            data.apiUrl,
            data.apiKey,
            data.eventTrackingAccountId,
            data.eventTrackingKey,
            data.defaultTeams instanceof Array ? {} : data.defaultTeams,
            data.defaultStatus,
            data.syncedRealworksAgendatypes,
            data.syncedRealworksTaaktypes,
            data.listId,
            data.agendaListId,
            data.taakListId,
            data.viewingListId,
            data.emmaListId,
            data.emmaDefaults instanceof Array ? {} : data.emmaDefaults,
        )
    }

    static create(data: any): ActiveCampaignCredentials {
        return new ActiveCampaignCredentials(
            'company' in data ? data.company : null,
            'id' in data ? data.id : uuid(),
            'enabled' in data ? data.enabled : false,
            new Date(),
            'createdBy' in data ? data.createdBy : null,
            new Date(),
            'updatedBy' in data ? data.updatedBy : null,
            'includeInternalContacts' in data ? data.includeInternalContacts : true,
            'includeExternalContacts' in data ? data.includeExternalContacts : false,
            'includeRealworksRelaties' in data ? data.includeRealworksRelaties : false,
            'syncedRealworksKenmerken' in data ? data.syncedRealworksKenmerken : [],
            'excludedRealworksKenmerken' in data ? data.excludedRealworksKenmerken : [],
            'syncedRealworksRelatietypes' in data ? data.syncedRealworksRelatietypes : [],
            'syncedRealworksStatus' in data ? data.syncedRealworksStatus : true,
            'updatedFrom' in data ? data.updatedFrom : null,
            'updatedUntil' in data ? data.updatedUntil : null,
            null,
            null,
            null,
            null,
            {},
            ReferralStatus.PROCESSED,
            [],
            [],
            null,
            null,
            null,
            null,
            null,
            {},
        )
    }

    // Convince Typescript that it is indeed an instance of the sub class
    clone(): ActiveCampaignCredentials {
        return super.clone() as ActiveCampaignCredentials
    }
}

export class MailchimpCredentials extends Credentials {
    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public includeInternalContacts: boolean,
        public includeExternalContacts: boolean,
        public includeRealworksRelaties: boolean,
        public syncedRealworksKenmerken: string[],
        public updatedFrom: Date,
        public updatedUntil: Date,
        public apiKey: string|null,
        public server: string|null,
        public listId: string|null,
    ) { super(company, id, enabled, createdAt, createdBy, updatedAt, updatedBy, CredentialsType.MAILCHIMP) }

    static fromData(data: any): MailchimpCredentials {
        return new MailchimpCredentials(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.enabled,
            data.createdAt,
            data.createdBy ? User.fromData(data.createdBy) : null,
            data.updatedAt,
            data.updatedBy ? User.fromData(data.updatedBy) : null,
            data.includeInternalContacts,
            data.includeExternalContacts,
            data.includeRealworksRelaties,
            data.syncedRealworksKenmerken,
            data.updatedFrom,
            data.updatedUntil,
            data.apiKey,
            data.server,
            data.listId,
        )
    }

    static create(data: any): MailchimpCredentials {
        return new MailchimpCredentials(
            'company' in data ? data.company : null,
            'id' in data ? data.id : uuid(),
            'enabled' in data ? data.enabled : false,
            new Date(),
            'createdBy' in data ? data.createdBy : null,
            new Date(),
            'updatedBy' in data ? data.updatedBy : null,
            'includeInternalContacts' in data ? data.includeInternalContacts : true,
            'includeExternalContacts' in data ? data.includeExternalContacts : false,
            'includeRealworksRelaties' in data ? data.includeRealworksRelaties : false,
            'syncedRealworksKenmerken' in data ? data.syncedRealworksKenmerken : [],
            'updatedFrom' in data ? data.updatedFrom : null,
            'updatedUntil' in data ? data.updatedUntil : null,
            null,
            null,
            null,
        )
    }

    // Convince Typescript that it is indeed an instance of the sub class
    clone(): MailchimpCredentials {
        return super.clone() as MailchimpCredentials
    }
}

export class RealworksCredentials extends Credentials {
    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public apiKey: string,
        public description: string|null,
        public canRequestFacturen: boolean,
        public canRequestObjecten: boolean,
        public canRequestAgenda: boolean,
        public canRequestTaken: boolean,
        public canRequestRelaties: boolean,
        public agendaBranch: Branch|null,
        public wonenObjectBranch: Branch|null,
        public productCategories: ProductCategory[] = [],
    ) { super(company, id, enabled, createdAt, createdBy, updatedAt, updatedBy, CredentialsType.REALWORKS) }

    static fromData(data: any): RealworksCredentials {
        return new RealworksCredentials(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.enabled,
            data.createdAt,
            data.createdBy ? User.fromData(data.createdBy) : null,
            data.updatedAt,
            data.updatedBy ? User.fromData(data.updatedBy) : null,
            data.apiKey,
            data.description,
            data.canRequestFacturen ?? false,
            data.canRequestObjecten ?? false,
            data.canRequestAgenda ?? false,
            data.canRequestTaken ?? false,
            data.canRequestRelaties ?? false,
            data.agendaBranch ? Branch.fromData(data.agendaBranch) : null,
            data.wonenObjectBranch ? Branch.fromData(data.wonenObjectBranch) : null,
            data.productCategories ? data.productCategories.map((category: any) => ProductCategory.fromData(category)) : [],
        )
    }

    // Convince Typescript that it is indeed an instance of the sub class
    clone(): RealworksCredentials {
        return super.clone() as RealworksCredentials
    }
}

export class TypeformCredentials extends Credentials {
    constructor(
        public company: Company|null,
        public id: string,
        public enabled: boolean,
        public createdAt: Date,
        public createdBy: User|null,
        public updatedAt: Date,
        public updatedBy: User|null,
        public apiKey: string|null,
        public workspaceId: string|null,
        public firstViewingAgendaTypes: string[],
        public secondViewingAgendaTypes: string[],
    ) { super(company, id, enabled, createdAt, createdBy, updatedAt, updatedBy, CredentialsType.TYPEFORM) }

    static fromData(data: any): TypeformCredentials {
        return new TypeformCredentials(
            data.company ? Company.fromData(data.company) : null,
            data.id,
            data.enabled,
            data.createdAt,
            data.createdBy ? User.fromData(data.createdBy) : null,
            data.updatedAt,
            data.updatedBy ? User.fromData(data.updatedBy) : null,
            data.apiKey,
            data.workspaceId,
            data.firstViewingAgendaTypes,
            data.secondViewingAgendaTypes,
        )
    }

    static create(data: any): TypeformCredentials {
        return new TypeformCredentials(
            'company' in data ? data.company : null,
            'id' in data ? data.id : uuid(),
            'enabled' in data ? data.enabled : false,
            new Date(),
            'createdBy' in data ? data.createdBy : null,
            new Date(),
            'updatedBy' in data ? data.updatedBy : null,
            null,
            null,
            'firstViewingAgendaTypes' in data ? data.firstViewingAgendaTypes : [],
            'secondViewingAgendaTypes' in data ? data.secondViewingAgendaTypes : [],
        )
    }

    // Convince Typescript that it is indeed an instance of the sub class
    clone(): TypeformCredentials {
        return super.clone() as TypeformCredentials
    }
}
